import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";
import { VimeoPlayer } from "../ReactPlayer/VimeoPlayer";
import axios from "axios";
import { Spinner } from "react-bootstrap";
import "./teamBuildingActivityDetail.css";

function TeamBuildingActivityDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [activity, setActivity] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showSpinner, setShowSpinner] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);

  // --- LIGHTBOX STATE ---
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");

  // Define Brand Green Color Constant for consistency
  const BRAND_GREEN = "#28a745";

  const getYouTubeId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  const handleVideoReady = () => {
    setVideoLoaded(true);
  };

  // --- LIGHTBOX HANDLERS ---
  const openLightbox = (imageUrl) => {
    setSelectedImage(imageUrl);
    setLightboxOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setSelectedImage("");
    document.body.style.overflow = "auto";
  };

  const fetchActivityDetails = async (activitySlug) => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `https://admin.upgrade-skills.com/api/team-building-activities/${activitySlug}`
      );
      if (response.data) {
        setActivity(response.data);
        if (window.updateMetaTags && response.data.title) {
          window.updateMetaTags(
            response.data.title,
            response.data.description || ""
          );
        }
      } else {
        navigate("/team-building-activities");
      }
    } catch (error) {
      console.error("Error fetching activity details:", error);
      navigate("/team-building-activities");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (slug) {
      fetchActivityDetails(slug);
      window.scrollTo(0, 0);
    }
  }, [slug]);

  if (isLoading) {
    return (
      <div
        style={{
          minHeight: "50vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Spinner animation="border" variant="success" /> {/* Changed to success/green */}
      </div>
    );
  }

  if (!activity) {
    return null;
  }

  const mainVideo =
    activity.videos && activity.videos.length > 0 ? activity.videos[0] : null;

  const mainImage =
    activity.images && activity.images.length > 0
      ? activity.images[0].url
      : activity.image_url || "";

  const isYouTubeVideo =
    mainVideo &&
    mainVideo.url &&
    (mainVideo.url.includes("youtube.com") ||
      mainVideo.url.includes("youtu.be"));

  return (
    <div>
      {showSpinner && <LoadingSpinner />}

      {/* --- LIGHTBOX OVERLAY --- */}
      {lightboxOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            zIndex: 9999,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "20px",
          }}
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            style={{
              position: "absolute",
              top: "20px",
              right: "30px",
              background: "transparent",
              border: "none",
              color: "#fff",
              fontSize: "40px",
              cursor: "pointer",
              zIndex: 10000,
            }}
          >
            &times;
          </button>
          <img
            src={selectedImage}
            alt="Full Screen"
            style={{
              maxWidth: "90%",
              maxHeight: "90vh",
              objectFit: "contain",
              borderRadius: "5px",
              boxShadow: "0 0 20px rgba(255,255,255,0.1)",
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <div className="section page-banner">
        <img
          className="shape-1 animation-round"
          src={process.env.PUBLIC_URL + "/images/shape/shape-8.png"}
          alt="shape"
        />
        <div className="container">
          <div className="page-banner-content">
            <ul className="breadcrumb">
              <li>
                <Link to="/">Home</Link>
              </li>
              <li>
                <Link to="/team-building-activities">
                  Team Building Activities
                </Link>
              </li>
              <li className="active">{activity.title}</li>
            </ul>
            <h1 className="title">
              <span>{activity.title}</span>
            </h1>
          </div>
        </div>
      </div>

      <div className="section section-padding mt-n10">
        <div className="container">
          <div className="row">
            <div className="col-lg-8">
              <div className="courses-details">
                <h2
                  className="title"
                  style={{
                    marginBottom: "30px",
                    fontSize: "32px",
                    fontWeight: "600",
                  }}
                >
                  {activity.title}
                </h2>

                {/* Main Video or Image */}
                {mainVideo && mainVideo.url ? (
                  <div className="courses-details-images">
                    <div className="video-wrapper">
                      {isYouTubeVideo ? (
                        <iframe
                          src={`https://www.youtube.com/embed/${getYouTubeId(
                            mainVideo.url
                          )}?autoplay=0&rel=0&modestbranding=1`}
                          title={activity.title}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                          onLoad={handleVideoReady}
                        />
                      ) : (
                        <VimeoPlayer
                          url={mainVideo.url}
                          onReady={handleVideoReady}
                        />
                      )}
                      {!videoLoaded && (
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            backgroundColor: "#f8f9fa",
                            zIndex: 1,
                          }}
                        >
                          <Spinner animation="border" variant="success" />
                        </div>
                      )}
                    </div>
                  </div>
                ) : mainImage ? (
                  <div
                    className="courses-details-images"
                    style={{ height: "auto", paddingBottom: 0 }}
                  >
                    <img
                      src={mainImage}
                      alt={activity.title}
                      style={{
                        width: "100%",
                        height: "auto",
                        display: "block",
                        cursor: "pointer",
                      }}
                      onClick={() => openLightbox(mainImage)}
                    />
                    {/* {activity.category && (
                      <span className="tags" style={{ backgroundColor: BRAND_GREEN }}>
                        {activity.category}
                      </span>
                    )} */}
                  </div>
                ) : null}

                {/* Description */}
                {activity.description && (
                  <div
                    className="courses-details-content"
                    style={{ marginBottom: "40px" }}
                  >
                    <h3
                      className="title"
                      style={{
                        fontSize: "24px",
                        marginBottom: "15px",
                        fontWeight: "600",
                      }}
                    >
                      About
                    </h3>
                    <p
                      style={{
                        fontSize: "16px",
                        lineHeight: "1.8",
                        color: "#555",
                      }}
                    >
                      {activity.description}
                    </p>
                  </div>
                )}

                {/* Gallery */}
                {activity.images && activity.images.length > 1 && (
                  <div
                    className="activity-images-gallery"
                    style={{ marginTop: "40px" }}
                  >
                    <h3
                      className="title"
                      style={{
                        fontSize: "24px",
                        marginBottom: "20px",
                        fontWeight: "600",
                      }}
                    >
                      Gallery
                    </h3>
                    <div className="row" style={{ marginTop: "20px" }}>
                      {activity.images.map((img, index) => (
                        <div key={img.id} className="col-md-4 mb-4">
                          <img
                            src={img.url}
                            alt={`${activity.title} - Image ${index + 1}`}
                            onClick={() => openLightbox(img.url)}
                            style={{
                              width: "100%",
                              height: "250px",
                              objectFit: "cover",
                              borderRadius: "12px",
                              cursor: "pointer",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                              transition: "transform 0.2s ease",
                            }}
                            onMouseEnter={(e) =>
                              (e.target.style.transform = "scale(1.03)")
                            }
                            onMouseLeave={(e) =>
                              (e.target.style.transform = "scale(1)")
                            }
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Additional Videos */}
                {activity.videos && activity.videos.length > 1 && (
                  <div
                    className="activity-videos"
                    style={{ marginTop: "40px" }}
                  >
                    <h3
                      className="title"
                      style={{
                        fontSize: "24px",
                        marginBottom: "20px",
                        fontWeight: "600",
                      }}
                    >
                      Additional Videos
                    </h3>
                    <div className="row" style={{ marginTop: "20px" }}>
                      {activity.videos.slice(1).map((video) => {
                        const isYoutube =
                          video.url &&
                          (video.url.includes("youtube.com") ||
                            video.url.includes("youtu.be"));

                        return (
                          <div key={video.id} className="col-md-6 mb-4">
                            <div className="courses-details-images">
                              <div className="video-wrapper">
                                {isYoutube ? (
                                  <iframe
                                    src={`https://www.youtube.com/embed/${getYouTubeId(
                                      video.url
                                    )}?rel=0&modestbranding=1`}
                                    title={`${activity.title} - Video ${video.order_no}`}
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowFullScreen
                                  />
                                ) : (
                                  <VimeoPlayer url={video.url} />
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Learning Outcomes - UPDATED COLOR */}
                {activity.learning_outcomes &&
                  activity.learning_outcomes.length > 0 && (
                    <div
                      className="courses-details-content"
                      style={{ marginTop: "40px" }}
                    >
                      <h3
                        className="title"
                        style={{
                          fontSize: "24px",
                          marginBottom: "20px",
                          fontWeight: "600",
                        }}
                      >
                        Learning Outcomes
                      </h3>
                      <div className="learning-outcomes-grid">
                        {activity.learning_outcomes.map((outcome, index) => (
                          <div
                            key={index}
                            className="outcome-item"
                            style={{
                              padding: "15px 20px",
                              marginBottom: "12px",
                              backgroundColor: "#fff", // Changed to White/Clean
                              borderRadius: "8px",
                              display: "flex",
                              alignItems: "center",
                              border: "1px solid #eee",
                              borderLeft: `4px solid ${BRAND_GREEN}`, // Consistent Green
                              boxShadow: "0 2px 5px rgba(0,0,0,0.05)"
                            }}
                          >
                            <i
                              className="icofont-check-circled"
                              style={{
                                color: BRAND_GREEN, // Consistent Green
                                marginRight: "12px",
                                fontSize: "20px",
                              }}
                            />
                            <span style={{ fontSize: "16px", color: "#333" }}>
                              {outcome}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                {/* Safety Notes - UPDATED COLOR */}
                {activity.safety_notes && (
                  <div
                    className="courses-details-content"
                    style={{ marginTop: "40px" }}
                  >
                    <h3
                      className="title"
                      style={{
                        fontSize: "24px",
                        marginBottom: "15px",
                        fontWeight: "600",
                      }}
                    >
                      Safety Notes
                    </h3>
                    <div
                      style={{
                        padding: "20px",
                        backgroundColor: "#f8f9fa", // Neutral gray
                        borderRadius: "8px",
                        borderLeft: `4px solid ${BRAND_GREEN}`, // Consistent Green
                        // Removed the Yellow Warning theme
                      }}
                    >
                      <p
                        style={{
                          fontSize: "16px",
                          lineHeight: "1.8",
                          color: "#333", // Standard Text
                          margin: 0,
                        }}
                      >
                        {activity.safety_notes}
                      </p>
                    </div>
                  </div>
                )}

                {/* Cultural Notes - UPDATED COLOR */}
                {activity.cultural_notes && (
                  <div
                    className="courses-details-content"
                    style={{ marginTop: "40px" }}
                  >
                    <h3
                      className="title"
                      style={{
                        fontSize: "24px",
                        marginBottom: "15px",
                        fontWeight: "600",
                      }}
                    >
                      Cultural Notes
                    </h3>
                    <div
                      style={{
                        padding: "20px",
                        backgroundColor: "#f8f9fa", // Neutral gray
                        borderRadius: "8px",
                        borderLeft: `4px solid ${BRAND_GREEN}`, // Consistent Green
                        // Removed the Teal/Info theme
                      }}
                    >
                      <p
                        style={{
                          fontSize: "16px",
                          lineHeight: "1.8",
                          color: "#333", // Standard Text
                          margin: 0,
                        }}
                      >
                        {activity.cultural_notes}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar - UPDATED ICONS TO GREEN */}
            <div className="col-lg-4">
              <div
                className="courses-sidebar"
                style={{ position: "sticky", top: "20px" }}
              >
                {/* Key Information */}
                <div
                  className="sidebar-widget"
                  style={{ marginBottom: "25px" }}
                >
                  <h3
                    className="widget-title"
                    style={{
                      fontSize: "20px",
                      marginBottom: "20px",
                      paddingBottom: "15px",
                      borderBottom: "2px solid #e9ecef",
                    }}
                  >
                    Activity Details
                  </h3>
                  <div className="widget-content">
                    {activity.team_size_min && activity.team_size_max && (
                      <div
                        className="info-item"
                        style={{
                          marginBottom: "18px",
                          paddingBottom: "18px",
                          borderBottom: "1px solid #e9ecef",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom: "5px",
                          }}
                        >
                          <i
                            className="icofont-group"
                            style={{
                              marginRight: "12px",
                              fontSize: "18px",
                              color: BRAND_GREEN, // GREEN
                            }}
                          />
                          <strong style={{ fontSize: "15px", color: "#333" }}>
                            Team Size
                          </strong>
                        </div>
                        <div
                          style={{
                            paddingLeft: "30px",
                            fontSize: "14px",
                            color: "#666",
                          }}
                        >
                          {activity.team_size_min} - {activity.team_size_max}{" "}
                          people
                        </div>
                      </div>
                    )}
                    {activity.indoor_or_outdoor && (
                      <div
                        className="info-item"
                        style={{
                          marginBottom: "18px",
                          paddingBottom: "18px",
                          borderBottom: "1px solid #e9ecef",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom: "5px",
                          }}
                        >
                          <i
                            className="icofont-location-pin"
                            style={{
                              marginRight: "12px",
                              fontSize: "18px",
                              color: BRAND_GREEN, // GREEN
                            }}
                          />
                          <strong style={{ fontSize: "15px", color: "#333" }}>
                            Location
                          </strong>
                        </div>
                        <div
                          style={{
                            paddingLeft: "30px",
                            fontSize: "14px",
                            color: "#666",
                            textTransform: "capitalize",
                          }}
                        >
                          {activity.indoor_or_outdoor}
                        </div>
                      </div>
                    )}
                    {(activity.instruction_time ||
                      activity.strategy_time ||
                      (activity.activity_time_min &&
                        activity.activity_time_max)) && (
                        <div
                          className="info-item"
                          style={{
                            marginBottom: "18px",
                            paddingBottom: "18px",
                            borderBottom: "1px solid #e9ecef",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              marginBottom: "8px",
                            }}
                          >
                            <i
                              className="icofont-clock-time"
                              style={{
                                marginRight: "12px",
                                fontSize: "18px",
                                color: BRAND_GREEN, // GREEN
                              }}
                            />
                            <strong style={{ fontSize: "15px", color: "#333" }}>
                              Duration
                            </strong>
                          </div>
                          <div
                            style={{
                              paddingLeft: "30px",
                              fontSize: "14px",
                              color: "#666",
                            }}
                          >
                            {activity.instruction_time && (
                              <div>
                                Instruction: {activity.instruction_time} min
                              </div>
                            )}
                            {activity.strategy_time && (
                              <div>Strategy: {activity.strategy_time} min</div>
                            )}
                            {activity.activity_time_min &&
                              activity.activity_time_max && (
                                <div>
                                  Activity: {activity.activity_time_min} -{" "}
                                  {activity.activity_time_max} min
                                </div>
                              )}
                          </div>
                        </div>
                      )}
                    {activity.space_required && (
                      <div
                        className="info-item"
                        style={{
                          marginBottom: "18px",
                          paddingBottom: "18px",
                          borderBottom: "1px solid #e9ecef",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom: "5px",
                          }}
                        >
                          <i
                            className="icofont-ruler"
                            style={{
                              marginRight: "12px",
                              fontSize: "18px",
                              color: BRAND_GREEN, // GREEN
                            }}
                          />
                          <strong style={{ fontSize: "15px", color: "#333" }}>
                            Space Required
                          </strong>
                        </div>
                        <div
                          style={{
                            paddingLeft: "30px",
                            fontSize: "14px",
                            color: "#666",
                          }}
                        >
                          {activity.space_required}
                        </div>
                      </div>
                    )}
                    {activity.props_required && (
                      <div
                        className="info-item"
                        style={{
                          marginBottom: "18px",
                          paddingBottom: "18px",
                          borderBottom: "1px solid #e9ecef",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom: "5px",
                          }}
                        >
                          <i
                            className="icofont-box"
                            style={{
                              marginRight: "12px",
                              fontSize: "18px",
                              color: BRAND_GREEN, // GREEN
                            }}
                          />
                          <strong style={{ fontSize: "15px", color: "#333" }}>
                            Props Required
                          </strong>
                        </div>
                        <div
                          style={{
                            paddingLeft: "30px",
                            fontSize: "14px",
                            color: "#666",
                          }}
                        >
                          {activity.props_required}
                        </div>
                      </div>
                    )}
                    {(activity.is_large_prop !== undefined ||
                      activity.physical_contact !== undefined ||
                      activity.is_messy_activity !== undefined) && (
                        <div
                          className="info-item"
                          style={{ marginBottom: "18px" }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              marginBottom: "8px",
                            }}
                          >
                            <i
                              className="icofont-info-circle"
                              style={{
                                marginRight: "12px",
                                fontSize: "18px",
                                color: BRAND_GREEN, // GREEN
                              }}
                            />
                            <strong style={{ fontSize: "15px", color: "#333" }}>
                              Additional Info
                            </strong>
                          </div>
                          <div
                            style={{
                              paddingLeft: "30px",
                              fontSize: "14px",
                              color: "#666",
                            }}
                          >
                            {activity.is_large_prop !== undefined && (
                              <div>
                                Large Props:{" "}
                                {activity.is_large_prop ? "Yes" : "No"}
                              </div>
                            )}
                            {activity.physical_contact !== undefined && (
                              <div>
                                Physical Contact:{" "}
                                {activity.physical_contact ? "Yes" : "No"}
                              </div>
                            )}
                            {activity.is_messy_activity !== undefined && (
                              <div>
                                Messy Activity:{" "}
                                {activity.is_messy_activity ? "Yes" : "No"}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                  </div>
                </div>

                {/* Learning Outcomes Sidebar - UPDATED */}
                {activity.learning_outcomes &&
                  activity.learning_outcomes.length > 0 && (
                    <div
                      className="sidebar-widget"
                      style={{ marginBottom: "25px" }}
                    >
                      <h3
                        className="widget-title"
                        style={{
                          fontSize: "20px",
                          marginBottom: "20px",
                          paddingBottom: "15px",
                          borderBottom: "2px solid #e9ecef",
                        }}
                      >
                        Key Outcomes
                      </h3>
                      <div className="widget-content">
                        {activity.learning_outcomes.map((outcome, index) => (
                          <div
                            key={index}
                            style={{
                              padding: "12px 15px",
                              marginBottom: "10px",
                              backgroundColor: "#f9f9f9", // Neutral Light
                              borderRadius: "8px",
                              fontSize: "14px",
                              color: "#333",
                              borderLeft: `3px solid ${BRAND_GREEN}`, // GREEN
                              fontWeight: "500",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
                            }}
                          >
                            {outcome}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                {/* Additional Cost Note */}
                {activity.additional_cost_note && (
                  <div
                    className="sidebar-widget"
                    style={{ marginBottom: "25px" }}
                  >
                    <h3
                      className="widget-title"
                      style={{
                        fontSize: "20px",
                        marginBottom: "20px",
                        paddingBottom: "15px",
                        borderBottom: "2px solid #e9ecef",
                      }}
                    >
                      Additional Information
                    </h3>
                    <div className="widget-content">
                      <p
                        style={{
                          fontSize: "14px",
                          lineHeight: "1.6",
                          color: "#666",
                          margin: 0,
                        }}
                      >
                        {activity.additional_cost_note}
                      </p>
                    </div>
                  </div>
                )}

                {/* Request Quote Button - UPDATED STYLE */}
                <div className="sidebar-widget" style={{ marginTop: "30px" }}>
                  <button
                    className="btn w-100"
                    style={{
                      backgroundColor: BRAND_GREEN,
                      color: "#fff",
                      fontSize: "16px",
                      fontWeight: "600",
                      borderRadius: "8px",
                      boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                      transition: "all 0.3s ease",
                      border: "none",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow =
                        "0 6px 12px rgba(0,0,0,0.15)";
                      e.currentTarget.style.backgroundColor = "#218838"; // Darker green on hover
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 6px rgba(0,0,0,0.1)";
                      e.currentTarget.style.backgroundColor = BRAND_GREEN;
                    }}
                    onClick={() => {
                      window.location.href = "/contact";
                    }}
                  >
                    Contact Us
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TeamBuildingActivityDetail;